<template>

  <v-card class="d-flex flex-column align-center justify-center signature-card">
    <!-- Title -->
    <v-card-title class="text-h5 signature-title" v-if="title || $slots.title">
      <slot name="title">
        {{ titleText }}
      </slot>
    </v-card-title>

    <!-- Canvas pad -->
    <div ref="sigPadContainer" class="signature-pad-container">
      <canvas
          ref="sigPad"
          class="signature-pad"
          aria-label="Signature drawing area">
      </canvas>
      <div class="signature-guide" aria-hidden="true">
        <span class="signature-guide-mark">X</span>
        <span class="signature-guide-line"></span>
      </div>
    </div>

    <!-- Actions -->
    <div class="signature-action-confirm" v-if='showReceiveConfirm'>
      <v-checkbox
          v-model="hasAgentConfirmed"
          hide-details
          :label='agentConfirmedLabel' />
    </div>
    <v-card-actions class="ma-0 pa-0 mt-6 signature-actions-area">
      <v-toolbar flat width="100%">
        <v-btn large text v-if="showCancel && !isUploading" @click="cancel">
          <v-icon left color="error">mdi-close</v-icon>
          Cancel
        </v-btn>
        <v-btn
            large
            text
            class="ml-4"
            v-if="showClear && !isUploading"
            @click="clear">
          <v-icon left color="amber darken-2">mdi-eraser</v-icon>
          Clear
        </v-btn>
        <v-spacer />
        <v-btn
            large
            color="primary"
            width="140"
            :disabled='disableSaveButton'
            @click="save">
          <span v-if="isUploading"><v-progress-circular indeterminate :size="16" color="white" /></span>
          <span v-else>Save</span>
        </v-btn>
      </v-toolbar>
    </v-card-actions>
  </v-card>

</template>

<script>
import SignaturePad from 'signature_pad'

export default {
  name: 'DeliverySignature',

  props: {
    title: {
      type: String,
      required: false
    },
    orderId: {
      type: [String, Number],
      required: true
    },
    isRejected: {
      type: Boolean,
      default: false
    },
    assetKey: {
      type: String,
      required: true
    },
    enableUpload: {
      type: Boolean,
      default: true
    },
    showClear: {
      type: Boolean,
      default: true
    },
    showCancel: {
      type: Boolean,
      default: true
    },
    showReceiveConfirm: {
      type: Boolean,
      default: false
    }
  },

  data () {
    return {
      sigPad: null,
      imageData: null,
      hasAgentConfirmed: false,
      isUploading: false,
      canvasWidth: 0,
      canvasHeight: 0,
      canvasPixelRatio: 0,
      resizeFrame: null
    }
  },

  computed: {
    titleText () {
      return this.title || ''
    },
    agentConfirmedLabel () {
      return (this.isRejected)
        ? 'I confirm that I am rejecting the delivery of this order.'
        : 'I confirm that I have received the contents of this order.'
    },
    disableSaveButton () {
      return this.showReceiveConfirm ? !this.hasAgentConfirmed : false
    }
  },

  mounted () {
    this.sigPad = new SignaturePad(this.$refs.sigPad, {
      backgroundColor: 'white',
      penColor: 'rgb(0,0,0)'
    })

    this.$nextTick(() => {
      this.resizeCanvas()
      window.addEventListener('resize', this.scheduleCanvasResize)

      if (window.ResizeObserver) {
        this.resizeObserver = new window.ResizeObserver(this.scheduleCanvasResize)
        this.resizeObserver.observe(this.$refs.sigPadContainer)
      }
    })
  },

  beforeDestroy () {
    window.removeEventListener('resize', this.scheduleCanvasResize)

    if (this.resizeObserver) this.resizeObserver.disconnect()
    if (this.resizeFrame) window.cancelAnimationFrame(this.resizeFrame)
    if (this.sigPad) {
      this.sigPad.off()
      this.clear()
    }
  },

  methods: {
    scheduleCanvasResize () {
      if (this.resizeFrame) window.cancelAnimationFrame(this.resizeFrame)
      this.resizeFrame = window.requestAnimationFrame(() => {
        this.resizeFrame = null
        this.resizeCanvas()
      })
    },

    resizeCanvas () {
      const canvas = this.$refs.sigPad
      if (!canvas || !this.sigPad) return

      const width = Math.floor(canvas.clientWidth)
      const height = Math.floor(canvas.clientHeight)
      const pixelRatio = Math.max(window.devicePixelRatio || 1, 1)

      if (!width || !height) return
      if (
        width === this.canvasWidth &&
        height === this.canvasHeight &&
        pixelRatio === this.canvasPixelRatio
      ) return

      const signatureData = this.sigPad.isEmpty() ? [] : this.sigPad.toData()
      const widthScale = this.canvasWidth ? width / this.canvasWidth : 1
      const heightScale = this.canvasHeight ? height / this.canvasHeight : 1
      const signatureScale = Math.min(widthScale, heightScale)
      const xOffset = (width - this.canvasWidth * signatureScale) / 2
      const yOffset = (height - this.canvasHeight * signatureScale) / 2

      canvas.width = Math.floor(width * pixelRatio)
      canvas.height = Math.floor(height * pixelRatio)
      canvas.getContext('2d').scale(pixelRatio, pixelRatio)

      this.canvasWidth = width
      this.canvasHeight = height
      this.canvasPixelRatio = pixelRatio

      if (signatureData.length) {
        const scaledData = signatureData.map(group => ({
          ...group,
          points: group.points.map(point => ({
            ...point,
            x: point.x * signatureScale + xOffset,
            y: point.y * signatureScale + yOffset
          }))
        }))
        this.sigPad.fromData(scaledData)
      } else {
        this.sigPad.clear()
      }
    },

    clear () {
      this.sigPad.clear()
      this.imageData = null
    },

    getSignatureImageData () {
      const prefix = 'data:image/svg+xml;base64,'
      const dataUrl = this.sigPad.toDataURL('image/svg+xml')
      const svg = window.atob(dataUrl.slice(prefix.length))
      // signature_pad v4 uses backing-store pixels for the SVG viewBox.
      // Restore CSS pixels so high-DPI signatures fill the exported image.
      const correctedSvg = svg.replace(
        /viewBox="[^"]+"/,
        `viewBox="0 0 ${this.canvasWidth} ${this.canvasHeight}"`
      )

      return prefix + window.btoa(correctedSvg)
    },

    cancel () {
      this.clear()
      this.$emit('canceled')
    },

    async save () {
      let uploaded = false
      let response = null
      let error = null
      this.imageData = this.getSignatureImageData()

      // Perform upload, if enabled
      if (this.enableUpload) {
        this.isUploading = true
        try {
          response = await this.uploadAsset()
          const status = response?.status || 'unknown'
          if (status === 200) uploaded = true
          else error = response?.statusText || `There was a problem uploading to storage (${status})`
        } catch (err) {
          error = err.text || err
          console.error('Error uploading asset =>', err)
        }
        this.isUploading = false
      }

      // Emit results
      this.$emit('saved', {
        order_id: this.orderId,
        asset_key: this.assetKey,
        image_data: this.imageData,
        terms: this.agentConfirmedLabel,
        uploaded,
        response,
        error
      })
    },

    async uploadAsset () {
      const orderId = this.orderId
      const assetKey = this.assetKey
      const imageData = this.imageData
      if (!assetKey || !orderId || !imageData) return false

      let uploadInfo = null
      try {
        const file = this.$root.utils.imageDataToFile(imageData)

        // Handle order-related upload
        uploadInfo = await this.$store.dispatch('sh/dataApi', {
          method: 'get',
          uri: `order/${orderId}/asset/uploadUrl`,
          opts: {
            params: { asset_key: assetKey }
          },
          to_model: false
        })

        return (uploadInfo)
          ? fetch(uploadInfo.url, { method: 'PUT', body: file, signal: AbortSignal.timeout(45000) })
          : false
      } catch (err) {
        console.error(`Error generating upload info for asset: ${assetKey} =>`, err)
        throw err
      }
    }
  }
}
</script>

<style scoped>
  .signature-card {
    box-sizing: border-box;
    width: 100%;
    height: 100vh;
    height: 100dvh;
    overflow: hidden;
    padding:
      env(safe-area-inset-top)
      env(safe-area-inset-right)
      env(safe-area-inset-bottom)
      env(safe-area-inset-left);
  }

  .signature-title {
    flex: 0 0 auto;
    justify-content: center;
    width: 100%;
    padding: 12px 16px;
  }

  .signature-pad-container {
    position: relative;
    flex: 0 0 auto;
    width: calc(100% - 32px);
    height: 200px;
    height: clamp(150px, 28vw, 240px);
    overflow: hidden;
    background-color: white;
    border-radius: 2px;
  }

  .signature-pad {
    display: block;
    width: 100%;
    height: 100%;
    touch-action: none;
  }

  .signature-guide {
    position: absolute;
    z-index: 1;
    right: 18%;
    bottom: calc(22% + 15px);
    left: 18%;
    display: flex;
    align-items: flex-end;
    gap: 8px;
    color: rgba(60, 60, 60, 0.55);
    pointer-events: none;
  }

  .signature-guide-mark {
    flex: 0 0 auto;
    padding-bottom: 1px;
    font-family: Georgia, 'Times New Roman', serif;
    font-size: 1.35rem;
    font-style: italic;
    line-height: 1;
  }

  .signature-guide-line {
    flex: 1 1 auto;
    margin-bottom: 3px;
    border-bottom: 1px solid rgba(60, 60, 60, 0.48);
  }

  .signature-actions-area ::v-deep .theme--dark.v-toolbar.v-sheet .v-toolbar__content {
    background-color: #1e1e1e;
    margin: 0 !important;
    padding: 0 !important;
  }

  .signature-action-confirm {
    flex: 0 0 auto;
    width: calc(100% - 32px);
    padding: 4px 0;
  }

  .signature-action-confirm ::v-deep .v-label {
    font-size: 1.24rem;
    line-height: 1.4;
  }

  .signature-action-confirm ::v-deep .v-input--selection-controls__input {
    width: 32px;
    height: 32px;
    margin-right: 10px;
  }

  .signature-action-confirm ::v-deep .v-icon {
    font-size: 30px;
  }

  .signature-actions-area {
    flex: 0 0 auto;
    width: calc(100% - 32px);
  }

  @media (orientation: landscape) {
    .signature-pad-container {
      height: clamp(130px, 38vh, 220px);
    }
  }

  @media (orientation: landscape) and (max-height: 500px) {
    .signature-title {
      min-height: 40px;
      padding-top: 4px;
      padding-bottom: 4px;
      font-size: 1.15rem !important;
    }

    .signature-action-confirm {
      padding: 0;
    }
  }
</style>
